import { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import SendFileAnimationComponent from "../../assets/lottie/SendFileAnimation/SendFileAnimationComponent";
import { getClientMessageDetails, sendReplyOnMessage } from "../../apis";
import { useParams } from "react-router-dom";
import RowLoader from "../UI/RowLoader";
import AnnouncementModal from "../AnnouncementModal";
import useGetRepliesForPartner from "./useGetRepliesForPartner";

const validationSchema = Yup.object().shape({
  message: Yup.string().required("Message is required"),
  attachment: Yup.mixed()
    .nullable()
    .notRequired()
    .test(
      "fileSize",
      "Attachment must be less than 2MB",
      (value) => !value || value.size <= 2048 * 1024
    ),
});
const ReplyOnMessage = ({ setPagination, setReplies }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState("");
  const { messageId } = useParams();
  const { fetchReplies } = useGetRepliesForPartner({
    messageId,
  });

  const onSubmitHandler = async (values, { resetForm }) => {
    const formData = new FormData();
    formData.append("message", values.message);
    if (values.attachment) {
      formData.append("message_attache", values.attachment);
    }

    setIsLoading(true);
    sendReplyOnMessage(messageId, formData, "client")
      .then(() => {
        resetForm();
        fetchReplies(1, true).then((result) => {
          if (result) {
            const { repliesData, pagination } = result;
            setReplies(repliesData);
            setPagination(pagination);
          }
        });
      })
      .catch((error) => {
        setIsError(error.response?.data?.message || error.message);
        setIsLoading(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Formik
      initialValues={{ message: "", attachment: null }}
      validationSchema={validationSchema}
      onSubmit={onSubmitHandler}
    >
      {({ handleSubmit, values, setFieldValue, errors, touched }) => (
        <Form
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        >
          <div className="tw-flex tw-gap-2 max-xl:tw-flex-col tw-mt-2 tw-mx-2">
            <div className="tw-w-full tw-relative tw-flex tw-items-center tw-gap-2">
              <Field
                name="message"
                as="textarea"
                rows={1}
                className={`!tw-h-28 tw-resize-none tw-w-full tw-bg-slate-200 !tw-rounded-md tw-placeholder:tw-text-slate-600 dark:tw-placeholder:tw-text-slate-300 tw-font-medium tw-pr-24 tw-pt-2 tw-pb-2 tw-pl-3 ${
                  errors.message && touched.message
                    ? "tw-border tw-border-red-500"
                    : ""
                }`}
                placeholder="Write a reply"
              />
            </div>

            {values.attachment && (
              <div className="tw-relative tw-w-1/4 max-md:tw-w-full tw-h-28 tw-rounded-md tw-overflow-hidden tw-flex-shrink-0">
                <img
                  onClick={() => setIsOpen(true)}
                  src={URL.createObjectURL(values.attachment)}
                  alt="attachment preview"
                  className="tw-w-full tw-h-full tw-object-cover tw-rounded-md tw-cursor-pointer"
                />
                <span
                  onClick={() => setFieldValue("attachment", null)}
                  className="material-icons tw-absolute tw-top-0 tw-right-0 tw-text-red-500 hover:tw-text-red-700 tw-cursor-pointer tw-bg-white tw-rounded-full tw-text-base tw-leading-none tw-p-[2px]"
                >
                  close
                </span>
              </div>
            )}

            {values.attachment && (
              <AnnouncementModal
                isOpen={isOpen}
                onClose={setIsOpen}
                imageSrc={URL.createObjectURL(values.attachment)}
              />
            )}
          </div>

          {/* Validation messages */}
          {errors.message && touched.message && (
            <p className="tw-text-red-500 tw-mt-1">{errors.message}</p>
          )}
          {errors.attachment && (
            <p className="tw-text-red-500 tw-mt-1">{errors.attachment}</p>
          )}
          {isError && <p className="tw-text-red-500 !tw-pt-2">{isError}</p>}

          <div className="tw-flex tw-w-full tw-items-center tw-gap-1 max-xl:!tw-justify-center tw-justify-end tw-my-4">
            <label htmlFor="file-upload" className="tw-cursor-pointer !tw-m-0">
              <div className="tw-border tw-flex tw-items-center tw-ps-3 tw-rounded-lg tw-text-[14px]">
                <span>Upload Attachment</span>
                <SendFileAnimationComponent className="tw-w-10 tw-h-10" />
              </div>
            </label>
            <input
              id="file-upload"
              type="file"
              accept="image/*"
              className="tw-hidden"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setFieldValue("attachment", file);
                }
              }}
            />

            <button
              type="submit"
              className={`!tw-m-0 !tw-w-[100px] !tw-rounded-lg tw-bg-primary tw-flex tw-justify-center tw-items-center tw-gap-2 !tw-px-3 tw-py-2 
                  ${values?.message ? "tw-text-white" : "tw-text-secondary"}`}
              disabled={!values?.message}
            >
              {isLoading ? (
                <div className="!tw-m-0 !tw-p-0">
                  <RowLoader paddingStyle="tw-py-0" />
                </div>
              ) : (
                <>
                  <span>Send</span>
                  <svg
                    className={`tw-w-[20px]  ${
                      values?.message ? "tw-fill-white" : "tw-fill-secondary"
                    }`}
                    viewBox="0 0 24 24"
                  >
                    <path d="M2,21L23,12L2,3V10L17,12L2,14V21Z" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default ReplyOnMessage;
